import React, { useState } from "react";
import { createPatient } from "./registerApi";

const PROVINCES = ["BC","AB","SK","MB","ON","QC","NB","NS","PEI","NL","YT","NT","NU"];
const GENDERS = ["male","female","other"];
const today = new Date().toISOString().split("T")[0];

const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&display=swap');

  .reg-root { background:#EDE3D7; min-height:100vh; padding:32px 16px; font-family:Georgia,serif; }
  .reg-wrap { max-width:740px; margin:0 auto; }

  .reg-card { background:white; border:1px solid #D9C9B4; border-radius:16px; overflow:hidden; margin-bottom:24px; }
  .reg-head { padding:28px 32px 24px; border-bottom:2px solid #C2651A; background:#FFFAF5; }
  .reg-eyebrow { font-size:10px; font-weight:600; letter-spacing:.16em; text-transform:uppercase; color:#C2651A; margin-bottom:10px; font-family:'DM Sans',sans-serif; }
  .reg-title { font-size:28px; color:#3D1F0A; line-height:1.1; letter-spacing:-0.5px; }
  .reg-subtitle { font-size:14px; color:#7A5230; margin-top:8px; font-family:'DM Sans',sans-serif; }

  .reg-section { padding:24px 32px; border-bottom:1px solid #EDE3D7; }
  .reg-section:last-child { border-bottom:none; }
  .reg-section-title { font-size:11px; font-weight:600; letter-spacing:.14em; text-transform:uppercase; color:#C2651A; margin-bottom:18px; font-family:'DM Sans',sans-serif; }

  .reg-grid { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
  .reg-grid.full { grid-template-columns:1fr; }
  @media(max-width:540px) { .reg-grid { grid-template-columns:1fr; } }

  .reg-field { display:flex; flex-direction:column; gap:6px; }
  .reg-label { font-size:12px; font-weight:600; color:#5C3A1E; font-family:'DM Sans',sans-serif; }
  .reg-label span { color:#C2651A; }
  .reg-input, .reg-select {
    padding:11px 14px; border:1.5px solid #D9C9B4; border-radius:8px;
    font-size:14px; font-family:'DM Sans',sans-serif; color:#3D1F0A;
    background:white; outline:none; transition:border-color .15s;
  }
  .reg-input:focus, .reg-select:focus { border-color:#C2651A; }
  .reg-input.error, .reg-select.error { border-color:#dc2626; }

  .reg-error-box {
    background:#FEF2F2; border:1px solid #FECACA; border-radius:8px;
    padding:12px 16px; margin-bottom:20px; font-size:13px; color:#B91C1C;
    font-family:'DM Sans',sans-serif;
  }

  .reg-submit-row { padding:24px 32px; background:#FFFAF5; border-top:1px solid #EDE3D7; }
  .reg-submit-btn {
    width:100%; padding:14px; background:#C2651A; color:white;
    border:none; border-radius:10px; font-size:16px; font-weight:600;
    font-family:'DM Sans',sans-serif; cursor:pointer; letter-spacing:0.02em;
    transition:background .2s, transform .15s, box-shadow .2s;
  }
  .reg-submit-btn:hover:not(:disabled) { background:#A0510E; transform:translateY(-1px); box-shadow:0 6px 18px rgba(194,101,26,.3); }
  .reg-submit-btn:disabled { background:#d4a574; cursor:not-allowed; }
`;

export default function RegistrationFormStep({ token, orgId, logo, orgInfo, onSuccess }) {
    const [form, setForm] = useState({
        firstname: "", lastname: "", phone: "", hin: "",
        birthday: "", gender: "male", province: "BC",
        alternativePhone: "", address: "", city: "", postal: "", email: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

    const required = ["firstname","lastname","phone","hin","birthday","province","address","city","postal","email"];
    const canSubmit = required.every((k) => form[k].trim()) && !submitting;

    const handleSubmit = async () => {
        setSubmitting(true);
        setError(null);
        try {
            const result = await createPatient({
                ...form,
                inviteToken: token,
                orgId,
            });

            if (result && result.status === 200) {
                const { intakeToken, orgId: returnedOrgId } = result.data || {};
                if (intakeToken) {
                    onSuccess(intakeToken, returnedOrgId || orgId);
                } else {
                    setError("Your account was created but we were unable to load the next step. Please contact your clinic.");
                }
                return;
            }

            const msg = result?.data?.message || result?.data?.error || "Registration failed. Please try again.";
            setError(msg);
        } catch (err) {
            setError("Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <style>{GLOBAL_CSS}</style>
            <div className="reg-root">
                <div className="reg-wrap">
                    <div className="reg-card">
                        <div className="reg-head">
                            <div className="reg-eyebrow">Patient Registration</div>
                            <div className="reg-title">Complete your registration</div>
                            <div className="reg-subtitle">
                                Please fill in your details below. All fields marked with * are required.
                            </div>
                        </div>

                        <div className="reg-section">
                            <div className="reg-section-title">Personal Information</div>
                            {error && <div className="reg-error-box">{error}</div>}
                            <div className="reg-grid">
                                <div className="reg-field">
                                    <label className="reg-label">First name <span>*</span></label>
                                    <input className="reg-input" value={form.firstname} onChange={set("firstname")} disabled={submitting} />
                                </div>
                                <div className="reg-field">
                                    <label className="reg-label">Last name <span>*</span></label>
                                    <input className="reg-input" value={form.lastname} onChange={set("lastname")} disabled={submitting} />
                                </div>
                                <div className="reg-field">
                                    <label className="reg-label">Health card number (HIN) <span>*</span></label>
                                    <input className="reg-input" value={form.hin} onChange={set("hin")} disabled={submitting} autoComplete="off" />
                                </div>
                                <div className="reg-field">
                                    <label className="reg-label">Date of birth <span>*</span></label>
                                    <input className="reg-input" type="date" max={today} value={form.birthday} onChange={set("birthday")} disabled={submitting} />
                                </div>
                                <div className="reg-field">
                                    <label className="reg-label">Gender</label>
                                    <select className="reg-select" value={form.gender} onChange={set("gender")} disabled={submitting}>
                                        {GENDERS.map((g) => <option key={g} value={g}>{g.charAt(0).toUpperCase() + g.slice(1)}</option>)}
                                    </select>
                                </div>
                                <div className="reg-field">
                                    <label className="reg-label">Province <span>*</span></label>
                                    <select className="reg-select" value={form.province} onChange={set("province")} disabled={submitting}>
                                        {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="reg-section">
                            <div className="reg-section-title">Contact Information</div>
                            <div className="reg-grid">
                                <div className="reg-field">
                                    <label className="reg-label">Email <span>*</span></label>
                                    <input className="reg-input" type="email" value={form.email} onChange={set("email")} disabled={submitting} />
                                </div>
                                <div className="reg-field">
                                    <label className="reg-label">Phone <span>*</span></label>
                                    <input className="reg-input" type="tel" value={form.phone} onChange={set("phone")} disabled={submitting} />
                                </div>
                                <div className="reg-field">
                                    <label className="reg-label">Alternative phone</label>
                                    <input className="reg-input" type="tel" value={form.alternativePhone} onChange={set("alternativePhone")} disabled={submitting} />
                                </div>
                            </div>
                        </div>

                        <div className="reg-section">
                            <div className="reg-section-title">Address</div>
                            <div className="reg-grid full">
                                <div className="reg-field">
                                    <label className="reg-label">Street address <span>*</span></label>
                                    <input className="reg-input" value={form.address} onChange={set("address")} disabled={submitting} />
                                </div>
                            </div>
                            <div className="reg-grid" style={{ marginTop: 16 }}>
                                <div className="reg-field">
                                    <label className="reg-label">City <span>*</span></label>
                                    <input className="reg-input" value={form.city} onChange={set("city")} disabled={submitting} />
                                </div>
                                <div className="reg-field">
                                    <label className="reg-label">Postal code <span>*</span></label>
                                    <input className="reg-input" value={form.postal} onChange={set("postal")} disabled={submitting} />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="reg-submit-row" style={{ background: "white", borderRadius: 12, border: "1px solid #D9C9B4", padding: "24px 32px" }}>
                        <button className="reg-submit-btn" disabled={!canSubmit} onClick={handleSubmit}>
                            {submitting ? "Creating your account…" : "Complete Registration →"}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}
